<script lang="ts" setup>
import { Swiper } from 'swiper'

import ProductTab from '~/enums/product-tab'
import type { ProductTabData } from '~/types/products'

const model = defineModel<ProductTab>()

const props = defineProps<{
  totalAttributes: number
  totalPublishedReviews: number
}>()

const sliderRef = ref<HTMLElement | null>(null)
const swiper = ref<Swiper | null>(null)

const productTabsData = computed(() => {
  const { Attributes, Description, Questions, Reviews } = ProductTab
  const { totalAttributes, totalPublishedReviews } = props

  return [
    { count: null, isEnabled: true, productTab: Description },
    { count: null, isEnabled: totalAttributes > 0, productTab: Attributes },
    { count: totalPublishedReviews, isEnabled: true, productTab: Reviews },
    { count: null, isEnabled: true, productTab: Questions }
  ].filter((x: ProductTabData): boolean => x.isEnabled)
})

onMounted((): void => {
  swiper.value = new Swiper(sliderRef.value as HTMLElement, { freeMode: true, slidesPerView: 'auto' })
})
</script>

<template>
  <section>
    <div class="container">
      <div ref="sliderRef" class="swiper-container flex">
        <div class="swiper-wrapper">
          <div
            v-for="x in productTabsData"
            :key="x.productTab"
            class="swiper-slide flex !w-auto cursor-pointer items-center justify-center bg-white"
            :class="{ active: model === x.productTab }"
            @click="model = x.productTab"
          >
            {{ ProductTab.getName(x.productTab) }}

            <div v-if="x.count !== null" class="count whitespace-nowrap">
              {{ x.count }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.container {
  @media screen and (max-width: 600px) {
    padding: 0;
  }
}

.swiper-container {
  font-size: 16px;

  @media screen and (max-width: 600px) {
    padding: 0 16px;
  }
}

.swiper-slide {
  border-radius: 12px;
  border: 1px solid white;
  box-sizing: border-box;
  font-weight: 700;
  margin-right: 8px;
  padding: 10px 16px;

  &:hover {
    color: variables.$main-green-color;
  }

  &.active {
    border: 1px solid variables.$main-green-color;
    color: variables.$main-green-color;
  }

  .count {
    background: color.mix(variables.$main-gray-color, white, 60%);
    border-radius: 1em;
    color: white;
    display: inline-block;
    font-size: .75em;
    line-height: 1em;
    margin-left: 8px;
    padding: 4px 8px;
  }
}
</style>
